import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Grid,
  Paper,
} from "@mui/material";
import {
  User,
  Calendar,
  ChevronRight,
  ChevronLeft,
  FileText,
} from 'lucide-react';
import { EntitiesListResponseModel } from "../../utils/models/EntitiesListResponseModel";
import { UserModel } from "../../utils/models/UserModel";
import { getReservationByUserId } from "../reservations/ReservationsServices";
import { ReservationModel } from "../../utils/models/ReservationModel";
import { USDollar, getDateSting } from "../../utils/functions/Helpers";
import InvoiceForm from "./InvoiceForm";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";

const steps = ['Identity Selection', 'Engagement Identification', 'Financial Finalization'];

const MultiStepsInvoiceForm = (props) => {
  const { setState, toggleModal, usersData } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [activeStep, setActiveStep] = useState(0);
  const [user, setUser] = useState(UserModel);
  const [reservationData, setReservationData] = useState(
    EntitiesListResponseModel
  );
  const setIsLoading = useLoadingSpinner();
  const [reservation, setReservation] = useState(ReservationModel);

  const enableNextButton = () => {
    if (activeStep === 0) return !!user.userId && !!reservation.id;
    return true;
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSelectUserChange = (e) => {
    const selectedUser = usersData.content.find(
      (u) => u.userId === e.target.value
    );
    setUser(selectedUser || UserModel);
    setReservation(ReservationModel); // Reset reservation when user changes
  };

  const onSelectReservationChange = (e) => {
    const selectedRes = reservationData.content.find(
      (r) => r.id === Number.parseInt(e.target.value, 10)
    );
    setReservation(selectedRes || ReservationModel);
  };

  const hasReservations = reservationData.content?.length > 0;

  useEffect(() => {
    if (user.userId) {
      getReservationByUserId(
        setReservationData,
        user.userId,
        undefined,
        setIsLoading,
        { pageSize: "All", page: 0 }
      );
    }
  }, [user.userId, setIsLoading]);

  const renderReservationSelectionContent = () => {
    if (user.userId) {
      if (hasReservations) {
        return (
          <FormControl fullWidth variant="outlined">
            <InputLabel id="res-select-label">Select Reservation</InputLabel>
            <Select
              labelId="res-select-label"
              value={reservation.id || ""}
              onChange={onSelectReservationChange}
              label="Select Reservation"
              sx={{ borderRadius: "12px" }}
            >
              <MenuItem value="">Select Reservation</MenuItem>
              {reservationData.content.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {getDateSting(r.startingDateTime)} — {r.eventType} — {USDollar.format(r.totalPrice)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography color="error" fontWeight="600">No active reservations found for this host.</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography color={colors.grey[400]}>Select a host first to see reservations.</Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={4}>
        <FileText size={32} color={colors.greenAccent[500]} />
        <Box>
          <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
            Guided Invoicing
          </Typography>
          <Typography variant="body1" sx={{ color: colors.grey[300] }}>
            Follow the strategic sequence to finalize the fiscal request.
          </Typography>
        </Box>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  '&.Mui-active': { color: colors.greenAccent[500] },
                  '&.Mui-completed': { color: colors.greenAccent[500] },
                }
              }}
            >
              <Typography fontWeight="600">{label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: '300px' }}>
        {activeStep === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
                <Typography variant="h6" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <User size={18} /> Host Selection
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="user-select-label">Select Event Host</InputLabel>
                  <Select
                    labelId="user-select-label"
                    value={user.userId || ""}
                    onChange={onSelectUserChange}
                    label="Select Event Host"
                    sx={{ borderRadius: "12px" }}
                  >
                    <MenuItem value="">Select Host</MenuItem>
                    {usersData.content.map((u) => (
                      <MenuItem key={u.userId} value={u.userId}>
                        {u.firstName} {u.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
                <Typography variant="h6" fontWeight="700" color={colors.greenAccent[500]} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calendar size={18} /> Active Reservation
                </Typography>
                {renderReservationSelectionContent()}
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && user.userId && reservation.id && (
          <Box sx={{ mt: -2 }}>
            <InvoiceForm
              user={user}
              reservation={reservation}
              setState={setState}
              toggleModal={toggleModal}
            />
          </Box>
        )}
      </Box>

      {activeStep === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            endIcon={<ChevronRight size={18} />}
            disabled={!enableNextButton()}
            onClick={handleNext}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: "700",
              bgcolor: colors.greenAccent[500],
              color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
              "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
            }}
          >
            Configure Financials
          </Button>
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ChevronLeft size={18} />}
            onClick={handleBack}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: "700",
              borderColor: colors.grey[400],
              color: colors.grey[100],
              "&:hover": { borderColor: colors.greenAccent[500], bgcolor: `${colors.greenAccent[500]}0D` }
            }}
          >
            Refine selection
          </Button>
        </Box>
      )}
    </Box>
  );
};

MultiStepsInvoiceForm.propTypes = {
  setState: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  usersData: PropTypes.object.isRequired,
};

export default MultiStepsInvoiceForm;
